import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash,
  PencilSimple,
  Stack,
  Lightning,
  CaretDown,
  CaretRight,
  CalendarBlank,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import WorkoutCard from "./WorkoutCard";
import ProgramVolumeSummary from "./ProgramVolumeSummary";
import { formatDateIT } from "@/lib/formatDate";

export default function ProgramListItem({
  program,
  index,
  workouts,
  isExpanded,
  completed,
  getPhaseTitle,
  exerciseLibrary,
  userMappings,
  onToggle,
  onAddWorkout,
  onEditProgram,
  onDeleteProgram,
  onToggleVisibility,
  onEditWorkout,
  onDeleteWorkout,
  onCopyWorkout,
  onSaveWorkoutAsTemplate,
  onWorkoutDragEnd,
  onLibraryUpdated,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );
  const hiddenFromClient = program.visible_to_client === false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-xl overflow-hidden transition-all ${
        completed
          ? "bg-[#0A0A0A] border border-[#1A1A1A] opacity-50 hover:opacity-75"
          : "bg-[#0F0F0F] border border-[#262626]"
      }`}
      data-testid={`program-card-${program.id}`}
    >
      <div
        className={`flex items-center justify-between px-5 py-4 cursor-pointer transition-colors ${
          completed ? "hover:bg-white/[0.01]" : "hover:bg-white/[0.02]"
        }`}
        onClick={() => onToggle(program.id)}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              completed
                ? "bg-zinc-800/50 border border-zinc-700/30"
                : "bg-gradient-to-br from-[#007AFF]/20 to-[#007AFF]/5 border border-[#007AFF]/20"
            }`}
          >
            <Stack
              size={20}
              weight="duotone"
              className={completed ? "text-zinc-600" : "text-[#007AFF]"}
            />
          </div>
          <div>
            <h3
              className={`font-bold text-base tracking-tight ${
                completed ? "text-zinc-500" : "text-white"
              }`}
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {program.title.toUpperCase()}
            </h3>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {program.phase_id && (
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                    completed
                      ? "bg-[#34C759]/10 text-[#34C759]/70"
                      : "bg-[#D4FF00]/10 text-[#D4FF00]"
                  }`}
                >
                  <Lightning size={10} weight="fill" />
                  {getPhaseTitle(program.phase_id)}
                </span>
              )}
              {(program.start_date || program.end_date) && (
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                    completed
                      ? "bg-zinc-800/50 text-zinc-500"
                      : "bg-[#007AFF]/10 text-[#007AFF]"
                  }`}
                  data-testid={`program-dates-${program.id}`}
                >
                  <CalendarBlank size={10} weight="fill" />
                  {formatDateIT(program.start_date) || "—"}
                  {" → "}
                  {formatDateIT(program.end_date) || "—"}
                </span>
              )}
              {completed && (
                <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-[#34C759]/10 text-[#34C759]/70">
                  completata
                </span>
              )}
              {hiddenFromClient && (
                <span
                  className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-orange-500/10 text-orange-400 flex items-center gap-1"
                  data-testid={`program-hidden-${program.id}`}
                >
                  <EyeSlash size={10} weight="fill" />
                  nascosto al cliente
                </span>
              )}
              <span
                className={`text-[11px] ${
                  completed ? "text-zinc-600" : "text-zinc-500"
                }`}
              >
                {workouts.length} schede
              </span>
              {program.description && (
                <span className="text-[11px] text-zinc-600 hidden sm:inline">
                  - {program.description}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddWorkout(program.id);
            }}
            className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-600 hover:text-[#007AFF] hover:bg-[#007AFF]/10 transition-all"
            data-testid={`add-workout-to-program-${program.id}`}
          >
            <Plus size={15} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(program);
            }}
            className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
              hiddenFromClient
                ? "text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                : "text-zinc-600 hover:text-[#007AFF] hover:bg-[#007AFF]/10"
            }`}
            title={
              hiddenFromClient
                ? "Mostra al cliente"
                : "Nascondi al cliente"
            }
            data-testid={`toggle-visibility-${program.id}`}
          >
            {hiddenFromClient ? <EyeSlash size={15} weight="fill" /> : <Eye size={15} />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditProgram(program);
            }}
            className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-600 hover:text-[#007AFF] hover:bg-[#007AFF]/10 transition-all"
            data-testid={`edit-program-${program.id}`}
          >
            <PencilSimple size={15} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteProgram(program.id);
            }}
            className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
            data-testid={`delete-program-${program.id}`}
          >
            <Trash size={15} />
          </button>
          {isExpanded ? (
            <CaretDown size={16} className="text-zinc-500" />
          ) : (
            <CaretRight size={16} className="text-zinc-500" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-3">
              {workouts.length > 0 ? (
                <>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => onWorkoutDragEnd(program.id, e)}
                  >
                    <SortableContext
                      items={workouts.map((w) => w.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {workouts.map((w) => (
                        <WorkoutCard
                          key={w.id}
                          workout={w}
                          getPhaseTitle={getPhaseTitle}
                          exerciseLibrary={exerciseLibrary}
                          userMappings={userMappings}
                          onEdit={onEditWorkout}
                          onDelete={onDeleteWorkout}
                          onCopy={onCopyWorkout}
                          onSaveAsTemplate={onSaveWorkoutAsTemplate}
                          sortable
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  <ProgramVolumeSummary
                    workouts={workouts}
                    exerciseLibrary={exerciseLibrary}
                    userMappings={userMappings}
                    onLibraryUpdated={onLibraryUpdated}
                  />
                </>
              ) : (
                <div className="text-center py-6 border border-dashed border-[#262626] rounded-lg">
                  <p className="text-zinc-600 text-sm">
                    Nessuna scheda in questo programma
                  </p>
                  <button
                    onClick={() => onAddWorkout(program.id)}
                    className="mt-1 text-[#007AFF] text-xs hover:text-[#3395FF]"
                    data-testid={`empty-add-workout-${program.id}`}
                  >
                    Aggiungi una scheda
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
